import { apiClient, apiStream } from '@/lib/api-client';

/**
 * Asking Juni, and reading the answer as it is written.
 *
 * ## The events, in order
 *
 * `text`, piece by piece, then `done`. `notice` replaces the text when Juni is busy or cannot
 * answer; `error` when an answer stopped part-way. The backend's `JuniChat` documents the same
 * order, and why the pages it read are not sent.
 *
 * ## Visitors and customers
 *
 * On the public site the question goes to the public endpoint, with no token, and the answer comes
 * from the documentation and the plans alone. In the dashboard it goes with the customer's token,
 * and the answer may describe their own servers, monitors, backups and subscription.
 */

export type JuniAudience = 'visitor' | 'customer';

export interface JuniTurn {
  role: 'user' | 'assistant';
  content: string;
}

export type JuniEvent =
  | { type: 'text'; text: string }
  | { type: 'notice'; text: string }
  | { type: 'error'; text: string }
  | { type: 'done' };

/** The same limit the server enforces, so the box stops where the server would refuse. */
export const MAX_QUESTION_CHARS = 1000;

/**
 * Whether this installation has Juni at all.
 *
 * False when it runs no model server (`JUNI_ENABLED=false`), and the widget is then not drawn:
 * a button that can never answer is worse than no button. A model server that is merely down is a
 * different thing — Juni says it cannot answer right now, and where to go instead.
 */
export async function juniAvailable(): Promise<boolean> {
  // A failed request throws rather than answering false: the caller retries it. Swallowing it hid
  // Juni for the rest of the tab from anybody whose page loaded while the API was restarting — a
  // deploy — though the installation had Juni all along. Only the API saying so hides it for good.
  const status = await apiClient.get<{ available: boolean }>('/juni/status', { skipAuth: true });

  return status.available;
}

function toEvent(name: string, data: unknown): JuniEvent | null {
  const payload = (data ?? {}) as Record<string, unknown>;

  switch (name) {
    case 'text':
    case 'notice':
    case 'error':
      return typeof payload.text === 'string' ? { type: name, text: payload.text } : null;
    case 'done':
      return { type: 'done' };
    default:
      return null;
  }
}

/** One `event:`/`data:` block of the stream, or null for anything else (comments, keep-alives). */
function parseBlock(block: string): JuniEvent | null {
  let name = 'message';
  const data: string[] = [];

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
  }

  if (data.length === 0) return null;

  try {
    return toEvent(name, JSON.parse(data.join('\n')));
  } catch {
    return null;
  }
}

/**
 * Sends the question and calls `onEvent` for each event as it arrives. Resolves when the stream
 * ends; throws `ApiError` when the question was refused (a rate limit, an empty question), and the
 * browser's own error when the connection fails.
 */
export async function askJuni(options: {
  audience: JuniAudience;
  question: string;
  history: JuniTurn[];
  signal: AbortSignal;
  onEvent: (event: JuniEvent) => void;
}): Promise<void> {
  const response = await apiStream(
    options.audience === 'visitor' ? '/juni/chat/public' : '/juni/chat',
    { question: options.question, history: options.history },
    { signal: options.signal, skipAuth: options.audience === 'visitor' },
  );

  if (response.body === null) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();

    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let boundary = buffer.indexOf('\n\n');

    while (boundary !== -1) {
      const event = parseBlock(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);

      if (event !== null) options.onEvent(event);

      boundary = buffer.indexOf('\n\n');
    }
  }

  const last = parseBlock(buffer + decoder.decode());

  if (last !== null) options.onEvent(last);
}
